import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ArrowRight, Award, Users, Building } from 'lucide-react';
import { mockCompanyInfo } from '../mock';

const Hero = () => {
  const { t } = useTranslation();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="inicio" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Main Heading */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t('hero.title')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600">
              {t('hero.subtitle')}
            </span>
          </h1>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            {t('hero.tagline')}
          </p>

          {/* Description */}
          <p className="text-lg text-gray-400 mb-12 max-w-4xl mx-auto leading-relaxed">
            {t('hero.description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={() => scrollToSection('#servicios')}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              {t('hero.viewServices')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => scrollToSection('#proyectos')}
              variant="outline"
              className="border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-xl"
            >
              {t('hero.viewProjects')}
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {mockCompanyInfo.stats.map((stat, index) => {
              const statKeys = ['experience', 'projects', 'cities', 'area'];
              return (
                <div key={index} className="text-center group">
                  <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 transition-all duration-300 hover:border-red-500 hover:shadow-lg">
                    <div className="text-3xl md:text-4xl font-bold text-red-500 mb-2 group-hover:scale-110 transition-transform duration-300">
                      {stat.value}
                    </div>
                    <div className="text-sm md:text-base text-gray-400 font-medium">
                      {t(`hero.stats.${statKeys[index]}`)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-16 text-gray-400">
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6 text-red-500" />
              <span className="font-medium">{t('hero.trustIndicators.nsr')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-red-500" />
              <span className="font-medium">{t('hero.trustIndicators.engineers')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-6 h-6 text-red-500" />
              <span className="font-medium">{t('hero.trustIndicators.projects')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-red-500 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-red-500 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;